<script setup>
import { inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import {
  ALWAYS_ENABLED_PROGRESS_MODULE_COLUMNS,
  PROGRESS_MODULE_COLUMN_IDS
} from '@/utils/custom-dashboard-helper'
import { customDashboardStateInjectionKey } from '@/utils/injection-keys'
import { progressModuleConfigurableColumnsPropValidator } from '@/utils/prop-validators'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import OkrIcon from '@/components/objectives/items/OkrIcon'
import TitleItems from '@/components/objectives/items/TitleItems'
import OkrElementOwner from '@/components/objectives/okr-elements-list/OkrElementOwner'
import CheckinCell from '@/components/objectives/table/cells/CheckinCell'
import GroupsCell from '@/components/objectives/table/cells/GroupsCell'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppTable from '@/components/ui/Table/AppTable'

defineOptions({
  name: 'ProgressModuleElementsView'
})

const props = defineProps({
  elements: {
    type: Array,
    required: true
  },

  enabledColumns: {
    type: Array,
    default: () => [],
    validator: v => progressModuleConfigurableColumnsPropValidator(v)
  },

  moduleId: {
    type: [String, Number],
    required: true
  }
})

const { t } = useI18n()

const tableColumns = computed(() => {
  return [
    {
      key: TABLE_COLUMNS_KEYS.NAME,
      width: 'auto'
    },

    {
      key: TABLE_COLUMNS_KEYS.OWNER,
      width: 32
    },

    {
      key: TABLE_COLUMNS_KEYS.GROUPS,
      width: 132
    },

    {
      key: TABLE_COLUMNS_KEYS.CHECKIN,
      width: 24
    }
  ]
    .map(column => {
      return {
        ...column,
        id: PROGRESS_MODULE_COLUMN_IDS[column.key] || null
      }
    })
    .filter(column => {
      return (
        props.enabledColumns.includes(column.id) ||
        ALWAYS_ENABLED_PROGRESS_MODULE_COLUMNS.includes(column.id)
      )
    })
})

const emit = defineEmits({
  'open-columns-settings': null
})

const customDashboardState = inject(customDashboardStateInjectionKey)

const isReadMode = computed(() => Boolean(customDashboardState.value.isReadMode))
</script>

<template>
  <div class="pmev-Wrapper">
    <AppTable
      :columns="tableColumns"
      :data="elements"
      :disable-user-select="false"
      border-on-last-row
      no-border
      type="primary-next"
    >
      <template #header-cells>
        <div class="pmev-HeadNameCell">
          {{ t('dashboard.elements') }}
        </div>
        <AppButton
          v-if="customDashboardState.userCanManageCustomDashboard && !isReadMode"
          :class="{
            'pmev-Trigger-active': customDashboardState.columnsSettingsModuleId === moduleId
          }"
          :size="BUTTON_SIZES.SM"
          class="pmev-Trigger"
          data-export-ignore
          height="24"
          icon="table-settings-next"
          type="ghost-next"
          width="24"
          @click="emit('open-columns-settings')"
        />
      </template>

      <template #cell="{ columnKey, item }">
        <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
          <div class="pmev-NameCell">
            <OkrIcon :objective="item" />

            <TitleItems
              :objective="item"
              :show-element-id="false"
              name-as-link
              with-tooltip
              @click-on-name="customDashboardState.editElement({ element: item })"
            />
          </div>
        </template>
        <template v-if="columnKey === TABLE_COLUMNS_KEYS.OWNER">
          <OkrElementOwner :okr-element="item" show-avatar-replacer />
        </template>
        <div v-if="columnKey === TABLE_COLUMNS_KEYS.GROUPS" class="pmev-GroupsCell">
          <GroupsCell :item="item" hide-group-icon />
        </div>

        <template v-if="columnKey === TABLE_COLUMNS_KEYS.CHECKIN">
          <CheckinCell
            :item="item"
            :preview-only="isReadMode"
            compact
            @open-checkin-modal="customDashboardState.openCheckinModal($event)"
          />
        </template>
      </template>
    </AppTable>
  </div>
</template>

<style lang="scss" scoped>
.pmev-Wrapper {
  --row-min-height: calc(var(--cdpm-row-height) - var(--cdpm-divider-size));
  --head-border-bottom: var(--cdpm-divider-size) solid var(--cdpm-divider-color);
  --head-padding-bottom: 0;
  --head-padding-right: calc(#{$page-right-padding} / 2);
  --head-padding-left: #{$page-left-padding};
  --row-padding: 0 calc(#{$page-right-padding} / 2) 0 #{$page-left-padding};
  --head-min-height: var(--cdpm-header-height);
  border-right: var(--cdpm-divider-size) solid var(--cdpm-divider-color);
}

.pmev-HeadNameCell {
  font-size: $fs-12;
  color: $dark-3;
  font-weight: fw('medium');
  line-height: 20px;
}

.pmev-NameCell {
  padding-right: var(--okr-table-row-gap);
  display: flex;
  align-items: center;
  gap: var(--okr-table-row-gap);
}

.pmev-GroupsCell {
  padding-right: var(--okr-table-row-gap);
  display: flex;
}

.pmev-Trigger {
  margin-left: auto;
  color: $dark-2;

  &-active {
    background-color: $dark-2;
    color: $white;
  }
}
</style>
